import React, { useCallback, useId, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import { LuX } from 'react-icons/lu';
import useDialogFocus from '../../hooks/useDialogFocus';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import IconButton from './IconButton';

const SIZES = {
  md: 'sm:tw-max-w-xl',
  lg: 'sm:tw-max-w-3xl',
  // Forms with a section list beside the fields
  form: 'sm:tw-max-w-4xl',
  xl: 'sm:tw-max-w-6xl',
  // Spreadsheet-like editors that need the whole screen width
  full: 'sm:tw-max-w-[min(96vw,110rem)]',
};

const ModalPanel = ({ onClose, title, subtitle, icon, actions, footer, size, closeLabel, lang, bodyClassName, dismissible, decoration, children }) => {
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const titleId = useId();
  // Always call the latest onClose without re-running the focus effect, which
  // would yank focus back to the close button on every parent render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // A non-dismissible dialog (a question that must be answered) ignores
  // Escape and backdrop clicks and has no close button
  const dismissibleRef = useRef(dismissible);
  dismissibleRef.current = dismissible;
  const handleClose = useCallback(() => {
    if (dismissibleRef.current) onCloseRef.current();
  }, []);
  useDialogFocus(panelRef, closeRef, handleClose);
  useBodyScrollLock();

  return (
    <motion.div
      lang={lang}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="tw-fixed tw-inset-0 tw-z-[1100] tw-flex tw-items-end tw-justify-center tw-font-sans tw-antialiased sm:tw-items-center sm:tw-p-6"
    >
      {/* The blur sits on a static child: blur and opacity animation on one element smear in Chromium */}
      <div aria-hidden="true" onClick={handleClose} className="tw-absolute tw-inset-0 tw-bg-slate-950/45 tw-backdrop-blur-sm dark:tw-bg-black/65" />

      {/* Drawn over the dimmed page but under the panel, so it never covers the dialog.
          `isolate` keeps the art's own z-indexes inside this layer. */}
      {decoration && (
        <div aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-inset-0 tw-isolate tw-overflow-hidden">
          {decoration}
        </div>
      )}

      <motion.div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        initial={{ opacity: 0, y: 40, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 24, scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        // overflow-clip (hidden where unsupported): a hidden-overflow box can still be
        // scrolled by focus/scrollIntoView, which slid the header out of view.
        className={`tw-relative tw-flex tw-max-h-[92vh] tw-w-full tw-flex-col tw-overflow-hidden tw-overflow-clip tw-rounded-t-[1.75rem] tw-border tw-border-white/80 tw-bg-white tw-text-slate-800 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_30px_80px_-20px_rgba(15,23,42,0.45)] focus:tw-outline-none sm:tw-max-h-[88vh] sm:tw-rounded-[1.75rem] dark:tw-border-white/10 dark:tw-bg-[#1f232b] dark:tw-text-slate-100 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_30px_80px_-20px_rgba(0,0,0,0.9)] ${SIZES[size]}`}
      >
        <div className="tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-gap-3 tw-border-b tw-border-slate-200/80 tw-px-5 tw-py-4 sm:tw-gap-4 sm:tw-px-6 dark:tw-border-white/10">
          {icon}
          <div className="tw-min-w-0 tw-flex-1">
            <h2 id={titleId} className="tw-truncate tw-text-lg tw-font-semibold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">
              {title}
            </h2>
            {subtitle && <p className="tw-truncate tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{subtitle}</p>}
          </div>
          {dismissible && (
            <IconButton ref={closeRef} label={closeLabel} onClick={handleClose} className="tw-order-2 sm:tw-order-3">
              <LuX aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
            </IconButton>
          )}
          {actions && (
            <div className="tw-order-3 tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-2 sm:tw-order-2 sm:tw-w-auto">
              {actions}
            </div>
          )}
        </div>

        <div className={`tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-overscroll-contain glass-scrollbar ${bodyClassName}`}>{children}</div>

        {footer && (
          <div className="tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-slate-200/80 tw-bg-slate-50/80 tw-px-5 tw-py-3.5 sm:tw-px-6 dark:tw-border-white/10 dark:tw-bg-white/[0.02]">
            {footer}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

/**
 * Portal-wide modal (Tailwind): portalled to <body> so no page stacking context
 * can trap it, a bottom sheet on phones and a centred card from `sm` up.
 * Escape closes the top-most modal only; the page doesn't scroll behind it.
 * `onClose` may be an inline function; the panel always calls the latest one.
 * `dismissible={false}`: no close button, Escape and backdrop clicks ignored.
 * `decoration`: purely decorative art around the dialog (between the dimmed page and the panel).
 */
const Modal = ({ isOpen, size = 'lg', bodyClassName = 'tw-p-5 sm:tw-p-6', dismissible = true, ...panelProps }) =>
  ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && <ModalPanel key="modal" size={size} bodyClassName={bodyClassName} dismissible={dismissible} {...panelProps} />}
    </AnimatePresence>,
    document.body
  );

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  icon: PropTypes.node,
  actions: PropTypes.node,
  footer: PropTypes.node,
  size: PropTypes.oneOf(Object.keys(SIZES)),
  closeLabel: PropTypes.string.isRequired,
  lang: PropTypes.string,
  bodyClassName: PropTypes.string,
  dismissible: PropTypes.bool,
  decoration: PropTypes.node,
  children: PropTypes.node,
};

export default Modal;
